'use client'

import { fadeUpSmooth } from '@/core/constants/animations'
import { cn } from '@/core/lib/utils/cn'
import { formatPrice } from '@/core/lib/utils/format-price'
import { AccountType } from '@/core/types/enums/account-type.enum'
import { Motion } from '@/core/ui/atoms/motion'
import { UserModel } from '@/features/auth/types/models/user.model'
import { PaymentMethod } from '@/features/reservation/types/enums/payment-method.enum'
import { CreatedTripModel } from '@/features/reservation/types/models/created-trip.model'
import { Chip } from '@heroui/react'
import { useTranslations } from 'next-intl'
import { PaymentMethodRadio } from './payment-method-radio'
import { radioClassNames } from './radio-classnames'

type WalletOptionsProps = {
  user: UserModel
  createdTrip: CreatedTripModel | null
  shouldShowRestForm: boolean
  walletIncluded: boolean
}

export const WalletOptions: React.FC<WalletOptionsProps> = ({
  user,
  createdTrip,
  shouldShowRestForm,
  walletIncluded,
}) => {
  const t = useTranslations()
  const walletBalance = user?.wallet || 0
  const credit = user?.creditLimit || 0
  const totalPrice = createdTrip?.totalPrice || 0
  const currency = createdTrip?.currency || user?.currency
  const accountType = user?.accountType
  const needsExtraPayment = walletIncluded && walletBalance < totalPrice
  const extraAmount = totalPrice - walletBalance

  return (
    <div>
      {accountType === AccountType.PROVIDER && (
        <div className='flex flex-col gap-1'>
          <div className='mb-3 flex items-center gap-3'>
            {t('profile.creditLimit')}:
            <Chip color='primary'>{formatPrice(credit, currency)}</Chip>
          </div>
          <div className='mb-3 flex items-center gap-3'>
            {t('profile.wallet')}:
            <Chip color='secondary'>
              {formatPrice(walletBalance, currency)}
            </Chip>
          </div>
        </div>
      )}
      <div
        className={cn(
          'flex flex-col gap-2',
          shouldShowRestForm
            ? 'border-sky-blue bg-pale-blue text-navy rounded border p-2'
            : '',
        )}
      >
        <PaymentMethodRadio
          color='danger'
          classNames={{
            ...radioClassNames(shouldShowRestForm ? 'navy' : 'grey'),
            labelWrapper: 'w-full ',
          }}
          bordered={!shouldShowRestForm}
          value={PaymentMethod.WALLET}
          isDisabled={
            shouldShowRestForm ||
            !walletBalance ||
            walletBalance + credit < totalPrice
          }
        >
          <span className='flex w-full items-center justify-between'>
            {accountType === AccountType.PROVIDER
              ? ` ${t('forms.walletAndCredit')}`
              : ` ${t('forms.wallet')}`}
            {accountType === AccountType.CUSTOMER && (
              <Chip
                color='default'
                classNames={{
                  base: shouldShowRestForm
                    ? 'bg-navy/30 px-4'
                    : 'bg-pale-blue px-4',
                  content: shouldShowRestForm ? 'text-navy' : 'text-sky-blue',
                }}
              >
                {formatPrice(walletBalance, currency)}
              </Chip>
            )}
          </span>
        </PaymentMethodRadio>

        {needsExtraPayment && (
          <Motion
            type='div'
            initial='initial'
            whileInView='animate'
            viewport={{ once: true }}
            variants={fadeUpSmooth.variants}
            className='flex flex-col gap-1 p-2'
          >
            <p>
              {t('trip.extraPayment', {
                amount: formatPrice(extraAmount, currency),
              })}
            </p>
            <PaymentMethodRadio
              color='secondary'
              squared
              classNames={{
                ...radioClassNames(shouldShowRestForm ? 'navy' : 'grey'),
                label: shouldShowRestForm && 'text-navy',
              }}
              value={PaymentMethod.WALLET_BANK_CARD}
              bordered
            >
              {t('forms.restBank')}
            </PaymentMethodRadio>

            <PaymentMethodRadio
              color='secondary'
              squared
              classNames={radioClassNames(shouldShowRestForm ? 'navy' : 'grey')}
              value={PaymentMethod.WALLET_PAYPAL}
              bordered
            >
              {t('forms.restPaypal')}
            </PaymentMethodRadio>
          </Motion>
        )}
      </div>
    </div>
  )
}
